import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardTitle } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { useCustomers } from "../../hooks/useCustomers";
import { TYPES } from "../../constants/wineTypes";
import { fetchDashboardAnalytics } from "./DashboardPage.api";
import type { DashboardAnalyticsData } from "./DashboardPage.types";

const LOOKBACK_DAYS = 30;

function formatCompactDate(day: string): string {
  const parsed = new Date(day);
  if (Number.isNaN(parsed.getTime())) {
    return day;
  }

  return parsed.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
  });
}

function formatSinceDate(days: number): string {
  const since = new Date();
  since.setDate(since.getDate() - days + 1);
  return since.toISOString().slice(0, 10);
}

function formatMetric(value: number): string {
  return new Intl.NumberFormat("fr-FR").format(value);
}

function getWineTypeLabel(type: string): string {
  return TYPES.find((item) => item.id === type)?.label || type;
}

function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

function DashboardFunnelChart({ analytics }: { analytics: DashboardAnalyticsData }) {
  const stages = [
    {
      id: "mails",
      label: "Emails envoyes",
      value: analytics.totals.uniqueMailTargets,
      detail: `${formatMetric(analytics.totals.mailsSent)} envois`,
      color: "bg-[#C7684F]",
      fill: "#C7684F",
    },
    {
      id: "opens",
      label: "Liens ouverts",
      value: analytics.totals.uniqueOpeners,
      detail: `${formatMetric(analytics.totals.linkOpens)} ouvertures`,
      color: "bg-[#2D6A9F]",
      fill: "#2D6A9F",
    },
    {
      id: "adds",
      label: "Ajouts au panier",
      value: analytics.totals.uniqueWineAdders,
      detail: `${formatMetric(analytics.totals.wineAddActions)} ajouts`,
      color: "bg-[#769C45]",
      fill: "#769C45",
    },
    {
      id: "orders",
      label: "Clics commander",
      value: analytics.totals.uniqueOrderClickers,
      detail: `${formatMetric(analytics.totals.orderClicks)} clics`,
      color: "bg-[#8E4A6B]",
      fill: "#8E4A6B",
    },
  ];

  const maxValue = Math.max(1, ...stages.map((stage) => stage.value));
  const stageHeights = stages.map((stage) => Math.max(28, Math.round((stage.value / maxValue) * 100)));
  const chartWidth = 540;
  const columnWidth = chartWidth / stages.length;

  if (stages.every((stage) => stage.value === 0)) {
    return <div className="rounded-[20px] border border-dashed border-border bg-background/60 px-4 py-10 text-center text-sm text-secondary">Aucune activité mesurée sur la période.</div>;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_220px]">
      <div className="rounded-[24px] border border-border bg-background/75 p-4 sm:p-5">
        <div className="overflow-hidden rounded-[24px] border border-border/80 bg-[linear-gradient(180deg,rgba(255,252,246,0.85),rgba(255,252,246,0.68))] px-3 py-4 dark:bg-[linear-gradient(180deg,rgba(43,24,32,0.82),rgba(32,18,24,0.75))]">
          <svg viewBox="0 0 540 160" className="h-[220px] w-full" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Tunnel de conversion des clients horizontal">
            {stages.map((stage, index) => {
              const currentHeight = stageHeights[index];
              const nextHeight = stageHeights[index + 1] ?? Math.max(18, Math.round(currentHeight * 0.72));
              const leftInset = (100 - currentHeight) / 2;
              const rightInset = (100 - nextHeight) / 2;
              const x1 = index * columnWidth;
              const x2 = (index + 1) * columnWidth;
              const previousValue = index === 0 ? stage.value : stages[index - 1].value;
              const stepRate = previousValue > 0 ? (stage.value / previousValue) * 100 : 0;
              const overallRate = stages[0].value > 0 ? (stage.value / stages[0].value) * 100 : 0;
              const topY1 = leftInset * 1.6;
              const bottomY1 = 160 - leftInset * 1.6;
              const topY2 = rightInset * 1.6;
              const bottomY2 = 160 - rightInset * 1.6;
              const polygonPoints = `${x1},${topY1} ${x2},${topY2} ${x2},${bottomY2} ${x1},${bottomY1}`;
              const centerX = x1 + columnWidth / 2;

              return (
                <g key={stage.id}>
                  <polygon
                    points={polygonPoints}
                    fill={stage.fill}
                    fillOpacity={0.86}
                    stroke="rgba(255,252,246,0.65)"
                    strokeWidth="0.8"
                  />
                  <line x1={x2} y1={topY2} x2={x2} y2={bottomY2} stroke="rgba(255,252,246,0.18)" strokeWidth="0.5" />
                  <text x={centerX} y="56" textAnchor="middle" fill="#FFF6EE" fontSize="8.4" fontWeight="700" letterSpacing="0.5">
                    {stage.label.toUpperCase()}
                  </text>
                  <text x={centerX} y="84" textAnchor="middle" fill="#FFF6EE" fontSize="13.2" fontWeight="800">
                    {formatMetric(stage.value)}
                  </text>
                  <text x={centerX} y="108" textAnchor="middle" fill="rgba(255,246,238,0.86)" fontSize="6.1">
                    {index === 0 ? stage.detail : `${formatPercent(stepRate)} de l'etape precedente`}
                  </text>
                  {index > 0 && (
                    <text x={centerX} y="126" textAnchor="middle" fill="rgba(255,246,238,0.72)" fontSize="5.2" letterSpacing="0.35">
                      {`${formatPercent(overallRate)} DU TOTAL INITIAL`}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      <div className="rounded-[24px] border border-border bg-background/75 p-5">
        <div className="text-[12px] uppercase tracking-[0.2em] text-tertiary">Synthese</div>
        <div className="mt-4 space-y-4">
          {stages.slice(1).map((stage, index) => {
            const sourceValue = stages[index].value;
            const rate = sourceValue > 0 ? (stage.value / sourceValue) * 100 : 0;

            return (
              <div key={stage.id}>
                <div className="flex items-center justify-between text-[13px] text-secondary">
                  <span>{stages[index].label} → {stage.label}</span>
                  <span className="font-semibold text-foreground">{formatPercent(rate)}</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-foreground/8">
                  <div className={`h-full rounded-full ${stage.color}`} style={{ width: `${Math.max(rate, rate > 0 ? 6 : 0)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle, accentClass }: { title: string; value: number; subtitle: string; accentClass: string }) {
  return (
    <Card className="overflow-hidden border-border/80 bg-surface/75">
      <div className={`h-1.5 w-full ${accentClass}`} />
      <div className="p-5">
        <div className="text-[12px] uppercase tracking-[0.22em] text-tertiary">{title}</div>
        <div className="mt-3 font-display text-[34px] font-bold tracking-[-0.05em] text-foreground">{formatMetric(value)}</div>
        <div className="mt-2 text-[13px] leading-[1.6] text-secondary">{subtitle}</div>
      </div>
    </Card>
  );
}

export function DashboardPage() {
  const { customers, loading: customersLoading, fetchCustomers } = useCustomers();
  const [analytics, setAnalytics] = useState<DashboardAnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const after = useMemo(() => formatSinceDate(LOOKBACK_DAYS), []);

  const customerHashes = useMemo(
    () => customers.map((customer) => customer.publicCarteHash).filter(Boolean),
    [customers]
  );

  useEffect(() => {
    fetchCustomers().catch((nextError: Error) => {
      setError(nextError.message || "Impossible de charger les clients.");
    });
  }, [fetchCustomers]);

  useEffect(() => {
    if (customerHashes.length === 0) {
      return;
    }

    let cancelled = false;

    async function loadAnalytics() {
      setLoading(true);
      setError(null);
      try {
        const nextAnalytics = await fetchDashboardAnalytics(after);
        if (!cancelled) {
          setAnalytics(nextAnalytics);
        }
      } catch (nextError: any) {
        if (!cancelled) {
          setError(nextError?.message || "Impossible de charger les analytics PostHog.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadAnalytics();

    return () => {
      cancelled = true;
    };
  }, [after, customerHashes]);

  return (
    <div className="mx-auto flex overflow-y-auto h-full w-full  flex-1 flex-col gap-6 px-5 py-8 lg:px-8">
      <div className="flex flex-col gap-3 rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,rgba(45,106,159,0.16),transparent_40%),linear-gradient(180deg,rgba(255,252,246,0.85),rgba(255,252,246,0.7))] p-6 shadow-[0_20px_60px_rgba(26,16,8,0.06)] dark:bg-[radial-gradient(circle_at_top_left,rgba(45,106,159,0.24),transparent_40%),linear-gradient(180deg,rgba(43,24,32,0.9),rgba(32,18,24,0.88))]">
        <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#2D6A9F]/20 bg-[#2D6A9F]/10 px-3 py-1 text-[12px] font-semibold text-[#2D6A9F]">
          <span className="h-2 w-2 rounded-full bg-[#2D6A9F]" />
          Activite clients sur {LOOKBACK_DAYS} jours
        </div>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="font-display text-[36px] font-bold tracking-[-0.05em] text-foreground">Suivi de votre carte publique</h2>
            <p className="mt-2 max-w-[760px] text-[15px] leading-[1.8] text-secondary">
              Le dashboard recoupe les partages email, les ouvertures de lien, les ajouts au panier et les clics sur commander pour tous vos clients reliés à votre espace.
            </p>
          </div>
          <Button type="button" variant="secondary" onClick={() => fetchCustomers().catch(() => {})}>
            Rafraichir les clients
          </Button>
        </div>
      </div>

      {customerHashes.length === 0 && !customersLoading && (
        <Card className="p-5">
          <CardHeader className="items-start gap-2">
            <CardTitle className="text-[14px] tracking-[0.2em] text-tertiary">Aucun client relie</CardTitle>
            <p className="text-sm leading-[1.8] text-secondary">
              Ajoutez au moins un client avec un lien de carte publique pour commencer a recevoir des donnees analytics exploitables.
            </p>
          </CardHeader>
        </Card>
      )}

      {error && (
        <Card className="border-[#C7684F]/20 bg-[#C7684F]/6 p-5">
          <CardHeader className="items-start gap-2">
            <CardTitle className="text-[14px] tracking-[0.2em] text-[#C7684F]">Erreur de chargement</CardTitle>
            <p className="text-sm leading-[1.8] text-secondary">{error}</p>
          </CardHeader>
        </Card>
      )}

      {analytics && (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard
              title="Emails"
              value={analytics.totals.mailsSent}
              subtitle={`${formatMetric(analytics.totals.uniqueMailTargets)} client${analytics.totals.uniqueMailTargets > 1 ? "s" : ""} cible${analytics.totals.uniqueMailTargets > 1 ? "s" : ""}`}
              accentClass="bg-[#C7684F]"
            />
            <StatCard
              title="Ouvertures"
              value={analytics.totals.uniqueOpeners}
              subtitle={`${formatMetric(analytics.totals.linkOpens)} ouverture${analytics.totals.linkOpens > 1 ? "s" : ""} en tout`}
              accentClass="bg-[#2D6A9F]"
            />
            <StatCard
              title="Ajouts vin"
              value={analytics.totals.uniqueWineAdders}
              subtitle={`${formatMetric(analytics.totals.wineAddActions)} ajout${analytics.totals.wineAddActions > 1 ? "s" : ""} au panier`}
              accentClass="bg-[#769C45]"
            />
            <StatCard
              title="Clics commander"
              value={analytics.totals.uniqueOrderClickers}
              subtitle={`${formatMetric(analytics.totals.orderClicks)} clic${analytics.totals.orderClicks > 1 ? "s" : ""} sur le bouton`}
              accentClass="bg-[#8E4A6B]"
            />
          </div>

          <Card className="p-5">
            <CardHeader className="mb-5 items-start gap-2">
              <CardTitle className="text-[14px] tracking-[0.2em] text-tertiary">Tunnel de conversion</CardTitle>
              <p className="text-sm leading-[1.8] text-secondary">
                Vue entonnoir du parcours client depuis le {formatCompactDate(after)} entre l'envoi du lien, l'ouverture, l'ajout au panier et l'intention de commande.
              </p>
            </CardHeader>
            {loading ? <div className="py-12 text-center text-sm text-secondary">Chargement du tunnel…</div> : <DashboardFunnelChart analytics={analytics} />}
          </Card>

          <Card className="p-5">
            <CardHeader className="mb-5 items-start gap-2">
              <CardTitle className="text-[14px] tracking-[0.2em] text-tertiary">Vins les plus ajoutes</CardTitle>
              <p className="text-sm leading-[1.8] text-secondary">
                Detail des bouteilles qui reviennent le plus souvent dans les ajouts au panier sur votre carte publique.
              </p>
            </CardHeader>
            {analytics.wineActivity.length === 0 ? (
              <div className="rounded-[20px] border border-dashed border-border bg-background/60 px-4 py-10 text-center text-sm text-secondary">Aucun ajout de vin mesure pour le moment.</div>
            ) : (
              <div className="overflow-hidden rounded-[20px] border border-border">
                <table className="min-w-full divide-y divide-border text-left">
                  <thead className="bg-background/70 text-[11px] uppercase tracking-[0.18em] text-tertiary">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Vin</th>
                      <th className="px-4 py-3 font-semibold">Type</th>
                      <th className="px-4 py-3 font-semibold">Ajouts</th>
                      <th className="px-4 py-3 font-semibold">Personnes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-surface/70 text-[14px] text-foreground">
                    {analytics.wineActivity.map((wine) => (
                      <tr key={wine.wineId + wine.wineName}>
                        <td className="px-4 py-3 font-semibold">{wine.wineName}</td>
                        <td className="px-4 py-3 text-secondary">{getWineTypeLabel(wine.wineType)}</td>
                        <td className="px-4 py-3">{formatMetric(wine.addCount)}</td>
                        <td className="px-4 py-3">{formatMetric(wine.uniquePeople)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}